import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import ActionColumn from '../components/ActionColumn';
import { useGetRoleGroupsQuery } from '../features/graph/services/graphApi';
import CreateRoleGroup from '../features/roles/components/CreateRoleGroup';
import MembersPanel from '../features/roles/components/MembersPanel';
import OwnersPanel from '../features/roles/components/OwnersPanel';
import RoleACLPanel from '../features/roles/components/RoleACLPanel';
import RoleInfo from '../features/roles/components/RoleInfo';
import RoleSelect from '../features/roles/components/RoleSelect';
import SubmitRoleChanges from '../features/roles/components/SubmitRoleChanges';
import { clearRoleManagement } from '../features/roles/services/roleManagementSlice';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useSelectedGroup } from '../hooks/useSelectedGroup';

export default function Roles() {
  const { roleId } = useParams<{ roleId: string }>();
  const dispatch = useAppDispatch();

  const { data: roleGroups, isFetching } = useGetRoleGroupsQuery();
  // Resolve the selected role from the list, falling back to a direct fetch; returns
  // undefined once the search is cleared so RoleInfo + panels close. See useSelectedGroup.
  const role = useSelectedGroup(roleId, roleGroups);

  // Clear staged changes when the selected role changes (mirrors routes/users.tsx).
  useEffect(() => {
    dispatch(clearRoleManagement());
  }, [roleId, dispatch]);

  return (
    // Bound the page to the viewport (minus the sticky nav h-15 + container py-5)
    // and lay it out as a column so the body can flex-fill. This lets the right
    // column's Members list grow to the bottom of the screen without ever pushing
    // the page tall enough to scroll.
    <div className="w-full px-6 flex flex-col h-[calc(100vh-6.25rem)]">
      {/* Header: role search + summary + the single Submit action */}
      <div className="flex-none flex flex-row flex-wrap items-center gap-4 bg-slate-50 dark:bg-gray-800 rounded-lg w-full px-4 py-2">
        <div className="flex flex-row gap-4 shrink-0">
          {isFetching ? (
            'Loading...'
          ) : (
            <div className="w-80">
              <RoleSelect roleGroups={roleGroups} selectedId={roleId} />
            </div>
          )}
        </div>
        {/* RoleInfo grows into the space between the search and Submit, laying its
            fields out in two columns so the header stays short rather than tall. */}
        <div className="flex-1 min-w-0">
          <RoleInfo role={role} />
        </div>
        {/* RefreshRoleData (manual server re-sync) is intentionally hidden for now —
            optimistic cache updates keep the lists fresh after Submit. The component
            and its test are kept in the codebase to re-enable if needed. See ADR-0002. */}
        <ActionColumn>
          <CreateRoleGroup />
          <SubmitRoleChanges role={role} />
        </ActionColumn>
      </div>

      {/* Body: owners + members on the left, access (Associated ACL Memberships) on the right */}
      {role && (
        <div className="flex flex-1 min-h-0 flex-row justify-between pt-4 pb-4 items-stretch">
          {/* The body flex-fills the bounded page height. The left column's Members
              panel flex-fills the space below Owners, and the ACL card on the right is
              pinned to the row height via an absolute fill, so both scrollable lists run
              to the bottom of the screen instead of stopping short or running the page tall. */}
          <div className="flex flex-col gap-6 w-6/12 pr-4 min-h-0">
            <OwnersPanel role={role} />
            <MembersPanel role={role} />
          </div>
          <div className="w-6/12 pl-4">
            <div className="relative h-full">
              <div className="absolute inset-0">
                <RoleACLPanel role={role} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
